
import React from "react";
import { Project, Activity } from "@/types";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface ActivityGanttBarProps {
  activity: Activity;
  project: Project;
  startDate: Date;
  endDate: Date;
  timelineScale: number;
  stageIndex: number;
}

export const ActivityGanttBar: React.FC<ActivityGanttBarProps> = ({
  activity,
  project,
  startDate,
  endDate,
  timelineScale,
  stageIndex
}) => {
  const isMobile = useIsMobile();
  
  // Determine color based on activity status
  const bgColor = 
    activity.status === "completed" ? "bg-green-400" : 
    activity.status === "in-progress" ? "bg-blue-400" : 
    "bg-gray-200";
  
  // Calculate position based on stage and activity index
  const basePosition = 5 + (stageIndex) * 12; // Match stage positioning
  
  // Get activity index in stage
  const stage = project.stages.find(s => s.id === activity.stage);
  const stageActivities = stage?.activities || [];
  const activityIndex = stageActivities.findIndex(a => a.id === activity.id);
  
  // We'll show activities in a vertical stack within their stage's column
  const barPosition = basePosition;
  const barWidth = 10; // Match stage bar width

  return (
    <div className="flex items-center h-8 mb-2">
      {/* Activity label container */}
      <div className="pl-2 sm:pl-5 truncate w-1/4 min-w-16 max-w-48">
        <span className="text-xs sm:text-sm truncate">{activity.name}</span>
      </div>
      
      {/* Timeline container */}
      <div className="flex-1 relative h-5">
        {/* The actual bar */}
        <div 
          className={cn(
            "absolute h-full rounded-md",
            bgColor,
            "border border-gray-300" // Add border for better visibility
          )}
          style={{
            left: `${barPosition}%`,
            width: `${barWidth}%`,
          }}
        >
          {/* Status indicator */}
          <div className="h-full flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-white"></div>
          </div>
        </div>
      </div>
      
      {/* Status display */}
      <div className="text-xs text-right w-1/5 min-w-16 max-w-24 truncate">
        {activity.status === "completed" ? "Complete" :
         activity.status === "in-progress" ? "In Progress" :
         "Pending"}
      </div>
    </div>
  );
};
